import React, { useEffect, useState, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV_CONFIG, BRAND } from "../data/content";
import { trackEvent, EVENTS } from "@/utils/tracking";
import { getWhatsAppLink } from "@/utils/links";
import Img from "@/components/Img";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(null);
  const location = useLocation();
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setActiveDropdown(null); }, [location.pathname]);

  const enter = (name) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setActiveDropdown(name);
  };
  const leave = () => {
    closeTimer.current = setTimeout(() => setActiveDropdown(null), 180);
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#FAF6F1]/95 backdrop-blur-xl border-b border-[rgba(59,49,45,0.08)] shadow-[0_6px_24px_-18px_rgba(59,49,45,0.25)]"
          : "bg-[#FAF6F1]/85 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-20 md:h-[88px] flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 shrink-0">
          <Img
            imageKey="global.logo"
            defaultSrc={BRAND.logo}
            defaultAlt="Petals & Promises"
            className="h-10 md:h-12 w-auto"
            fit="contain"
            style={{ maxWidth: "220px" }}
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-6 flex-1 justify-center h-full">
          {NAV_CONFIG.map((l) => (
            <div
              key={l.name}
              className="relative h-full flex items-center"
              onMouseEnter={l.dropdown ? () => enter(l.name) : undefined}
              onMouseLeave={l.dropdown ? leave : undefined}
            >
              {l.dropdown ? (
                <button
                  type="button"
                  data-testid={`nav-link-${l.name.toLowerCase()}`}
                  className={`h-full inline-flex items-center gap-1 whitespace-nowrap text-[12px] tracking-[0.22em] uppercase font-semibold leading-none transition-colors ${
                    location.pathname.startsWith(l.path) && l.path !== "/" ? "text-[#B65980]" : "text-[#3B312D] hover:text-[#B65980]"
                  }`}
                  onClick={() => (window.location.href = l.path)}
                >
                  <span className="leading-none">{l.name}</span>
                  <ChevronDown size={13} className={`transition-transform ${activeDropdown === l.name ? "rotate-180" : ""}`} />
                </button>
              ) : (
                <NavLink
                  to={l.path}
                  data-testid={`nav-link-${l.name.toLowerCase()}`}
                  className={({ isActive }) =>
                    `h-full inline-flex items-center whitespace-nowrap text-[12px] tracking-[0.22em] uppercase font-semibold leading-none transition-colors ${
                      isActive ? "text-[#B65980]" : "text-[#3B312D] hover:text-[#B65980]"
                    }`
                  }
                  end={l.path === "/"}
                >
                  <span className="leading-none">{l.name}</span>
                </NavLink>
              )}

              {l.dropdown && activeDropdown === l.name && (
                <div
                  onMouseEnter={() => enter(l.name)}
                  onMouseLeave={leave}
                  className="absolute left-1/2 -translate-x-1/2 top-full mt-4 w-[360px] bg-[#FAF6F1] border border-[rgba(59,49,45,0.08)] rounded-2xl shadow-[0_24px_48px_-24px_rgba(59,49,45,0.35)] py-4 grid grid-cols-2 gap-x-2"
                  data-testid={`nav-dropdown-${l.name.toLowerCase()}`}
                >
                  {l.dropdown.map((d) => (
                    <Link
                      key={d.name}
                      to={d.path}
                      onClick={() => setActiveDropdown(null)}
                      className="px-5 py-2 text-[12.5px] text-[#3B312D] hover:text-[#B65980] hover:bg-[#F4DCD8]/30 transition rounded-lg"
                      data-testid={`nav-dropdown-item-${d.name.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {d.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden xl:flex items-center shrink-0">
          <Link to="/contact" data-testid="nav-plan-event-btn" onClick={() => trackEvent(EVENTS.PLAN_EVENT_CTA_CLICK, { location: "navbar" })} className="btn-primary !py-2.5 !px-5 !text-[11px]">
            Plan Your Event
          </Link>
        </div>

        <button
          className="xl:hidden inline-flex items-center justify-center w-11 h-11 rounded-full border border-[rgba(59,49,45,0.2)] text-[#3B312D] shrink-0"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden bg-[#FAF6F1] border-t border-[rgba(59,49,45,0.08)]">
          <div className="px-6 py-6 flex flex-col gap-2 max-h-[80vh] overflow-y-auto">
            {NAV_CONFIG.map((l) => (
              <div key={l.name} className="border-b border-[rgba(59,49,45,0.06)] py-3 last:border-0">
                <div className="flex items-center justify-between">
                  <NavLink
                    to={l.path}
                    data-testid={`nav-mobile-link-${l.name.toLowerCase()}`}
                    className={({ isActive }) =>
                      `text-sm tracking-[0.22em] uppercase font-semibold ${isActive ? "text-[#B65980]" : "text-[#3B312D]"}`
                    }
                    end={l.path === "/"}
                  >
                    {l.name}
                  </NavLink>
                  {l.dropdown && (
                    <button
                      onClick={() => setMobileOpen(mobileOpen === l.name ? null : l.name)}
                      className="text-[#3B312D] p-1"
                      data-testid={`nav-mobile-toggle-${l.name.toLowerCase()}`}
                    >
                      <ChevronDown size={16} className={`transition-transform ${mobileOpen === l.name ? "rotate-180" : ""}`} />
                    </button>
                  )}
                </div>
                {l.dropdown && mobileOpen === l.name && (
                  <div className="mt-3 grid grid-cols-2 gap-1">
                    {l.dropdown.map((d) => (
                      <Link key={d.name} to={d.path} className="py-1.5 text-[12px] text-[#3B312D]/80 hover:text-[#B65980]">
                        {d.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <Link to="/contact" data-testid="nav-mobile-plan-btn" onClick={() => trackEvent(EVENTS.PLAN_EVENT_CTA_CLICK, { location: "navbar-mobile" })} className="btn-primary mt-3 self-start">
              Plan Your Event
            </Link>
            <a
              href={getWhatsAppLink()}
              data-testid="nav-mobile-whatsapp"
              onClick={() => trackEvent(EVENTS.WHATSAPP_CLICK, { location: "navbar-mobile" })}
              className="text-sm text-[#8A9A7B] tracking-[0.22em] uppercase mt-2"
              target="_blank" rel="noreferrer noopener"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
